import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowRight } from "lucide-react";
import { Link } from "react-scroll";
import { useState, useEffect } from "react";
import heroImage from "../../../assests/images/rsc_image.png";
import { useNavigate } from "react-router-dom";

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <Box
      sx={{
        minHeight: { xs: "calc(100vh - 64px)", md: "90vh" },
        display: "flex",
        alignItems: "center",
        bgcolor: "background.default",
        position: "relative",
        pt: { xs: 16, sm: 12, md: 8 },
        pb: { xs: 8, md: 0 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Box
              sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transition: "all 0.8s cubic-bezier(0.17, 0.55, 0.55, 1)",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
                  lineHeight: 1.2,
                  mb: { xs: 2, md: 3 },
                  color: "white",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                }}
              >
                Streamline Your
                <Box component="span" sx={{ color: "#00ADB5" }}>
                  {" "}
                  Supply Chain
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: { xs: 3, md: 4 },
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.8s cubic-bezier(0.17, 0.55, 0.55, 1) 0.2s",
                  color: "text.secondary",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  maxWidth: { xs: "100%", md: "540px" },
                  mx: { xs: "auto", md: 0 },
                  fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                }}
              >
                No waiting, no negotiations, no unreliable buyers. Skip the
                hassle of online marketplaces. We buy your items instantly,
                handle logistics, and pay you in cash—faster and easier than
                eBay or Facebook Marketplace.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.8s cubic-bezier(0.17, 0.55, 0.55, 1) 0.4s",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: { xs: "center", md: "flex-start" },
                }}
              >
                <Button
                  variant="contained"
                  size={isMobile ? "medium" : "large"}
                  fullWidth={isMobile}
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.25, md: 1.5 },
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover .arrow": {
                      transform: "translateX(5px)",
                    },
                  }}
                  onClick={() => navigate("/signup")}
                >
                  Get Started
                  <ArrowRight
                    className="arrow"
                    size={isMobile ? 16 : 20}
                    style={{ transition: "transform 0.3s ease" }}
                  />
                </Button>
                <Link
                  to="about"
                  smooth={true}
                  duration={500}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  <Button
                    variant="outlined"
                    size={isMobile ? "medium" : "large"}
                    fullWidth={isMobile}
                    sx={{
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.25, md: 1.5 },
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    Learn More
                  </Button>
                </Link>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box
              sx={{
                position: "relative",
                height: { xs: "250px", sm: "300px", md: "500px" },
                width: "100%",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(50px)",
                transition: "all 1s cubic-bezier(0.17, 0.55, 0.55, 1) 0.2s",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: { xs: "12px", md: "16px" },
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <img
                  src={heroImage}
                  alt="Modern Warehouse Operations"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
